// Brutalist custom dropdown - wraps a native `<select>` so existing change
// listeners keep working. The native element stays in the DOM (hidden via
// `.sr-only`) and is the source of truth for `value`. The visual button +
// listbox mirror the current option label and dispatch `change` on the
// underlying select when a new option is picked.
//
// Programmatic writes (e.g. `select.value = 'B'`) need to call
// `select.dispatchEvent(new Event('cselect:sync'))` afterwards so the
// wrapper re-renders the button text. We deliberately don't observe via
// MutationObserver - the `value` is a property, not an attribute.

import { onLocaleChange } from "../i18n";

export interface CustomSelectHandle {
  root: HTMLDivElement;
  refresh(): void;
}

export function makeCustomSelect(
  select: HTMLSelectElement,
): CustomSelectHandle {
  // Hide the native select but keep it focusable / form-participating for the
  // small set of tests that introspect it. `display: none` would unset the
  // associated <label> click target so we use `.sr-only` instead.
  select.classList.add("sr-only");
  select.setAttribute("tabindex", "-1");
  select.setAttribute("aria-hidden", "true");

  const root = document.createElement("div");
  root.className = "cselect";

  const button = document.createElement("button");
  button.type = "button";
  button.className = "cselect-btn";
  button.setAttribute("aria-haspopup", "listbox");
  button.setAttribute("aria-expanded", "false");

  const buttonLabel = document.createElement("span");
  buttonLabel.className = "cselect-label";
  button.appendChild(buttonLabel);

  const caret = document.createElement("span");
  caret.className = "cselect-caret";
  caret.setAttribute("aria-hidden", "true");
  button.appendChild(caret);

  const menu = document.createElement("ul");
  menu.className = "cselect-menu";
  menu.setAttribute("role", "listbox");
  menu.hidden = true;

  root.appendChild(button);
  root.appendChild(menu);

  function currentOption(): HTMLOptionElement | null {
    return select.options[select.selectedIndex] ?? null;
  }

  function refreshButton(): void {
    const opt = currentOption();
    buttonLabel.textContent = opt?.textContent ?? select.value;
  }

  function buildMenu(): void {
    menu.replaceChildren();
    Array.from(select.options).forEach((opt, idx) => {
      const li = document.createElement("li");
      li.setAttribute("role", "option");
      li.dataset["value"] = opt.value;
      li.dataset["idx"] = String(idx);
      li.tabIndex = -1;
      const isActive = opt.value === select.value;
      li.setAttribute("aria-selected", String(isActive));
      if (isActive) li.classList.add("is-active");
      const isDisabled = opt.disabled;
      if (isDisabled) {
        li.setAttribute("aria-disabled", "true");
      }

      const check = document.createElement("span");
      check.className = "cselect-check";
      check.setAttribute("aria-hidden", "true");
      check.textContent = isActive ? "▸" : "";
      li.appendChild(check);

      const label = document.createElement("span");
      label.textContent = opt.textContent ?? opt.value;
      li.appendChild(label);

      li.addEventListener("mousedown", (event) => {
        event.preventDefault();
      });
      if (!isDisabled) {
        li.addEventListener("click", () => {
          if (select.value !== opt.value) {
            select.value = opt.value;
            select.dispatchEvent(new Event("change", { bubbles: true }));
          }
          close();
        });
      }
      menu.appendChild(li);
    });
  }

  let isOpen = false;

  function open(): void {
    if (isOpen) return;
    isOpen = true;
    buildMenu();
    menu.hidden = false;
    button.setAttribute("aria-expanded", "true");
    document.addEventListener("mousedown", onDocMouseDown, true);
    document.addEventListener("keydown", onDocKey, true);
  }

  function close(): void {
    if (!isOpen) return;
    isOpen = false;
    menu.hidden = true;
    button.setAttribute("aria-expanded", "false");
    document.removeEventListener("mousedown", onDocMouseDown, true);
    document.removeEventListener("keydown", onDocKey, true);
  }

  function onDocMouseDown(event: MouseEvent): void {
    if (event.target instanceof Node && root.contains(event.target)) return;
    close();
  }

  function selectableItems(): HTMLLIElement[] {
    return Array.from(
      menu.querySelectorAll<HTMLLIElement>(
        "li[role='option']:not([aria-disabled='true'])",
      ),
    );
  }

  function focusOption(idx: number): void {
    const items = selectableItems();
    if (items.length === 0) return;
    const exact = items.find((item) => item.dataset["idx"] === String(idx));
    const target = exact ?? items[Math.max(0, Math.min(items.length - 1, idx))];
    if (!target) return;
    for (const item of items) item.tabIndex = -1;
    target.tabIndex = 0;
    target.focus();
  }

  function onDocKey(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      button.focus();
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      const items = selectableItems();
      if (items.length === 0) return;
      event.preventDefault();
      const active = document.activeElement;
      const currentIdx = items.findIndex((it) => it === active);
      const nextIdx =
        currentIdx === -1
          ? event.key === "ArrowDown"
            ? 0
            : items.length - 1
          : event.key === "ArrowDown"
            ? (currentIdx + 1) % items.length
            : (currentIdx - 1 + items.length) % items.length;
      for (const item of items) item.tabIndex = -1;
      const next = items[nextIdx];
      if (next) {
        next.tabIndex = 0;
        next.focus();
      }
    }
    if (event.key === "Enter" || event.key === " ") {
      const active = document.activeElement;
      if (active instanceof HTMLLIElement && menu.contains(active)) {
        event.preventDefault();
        active.click();
      }
    }
  }

  button.addEventListener("click", () => {
    if (isOpen) close();
    else {
      open();
      // Land focus on currently active option for keyboard nav.
      const activeIdx = Array.from(select.options).findIndex(
        (o) => o.value === select.value,
      );
      focusOption(Math.max(0, activeIdx));
    }
  });

  select.addEventListener("change", () => {
    refreshButton();
    if (isOpen) buildMenu();
  });
  select.addEventListener("cselect:sync", () => {
    refreshButton();
    if (isOpen) buildMenu();
  });

  // When the locale changes, the i18n sync inside controls.ts rewrites
  // `<option>` text - we re-render the button label so the visible text
  // stays in sync without needing the call site to fire `cselect:sync`.
  // Defer to a microtask so we read AFTER the controls.ts syncI18n hook
  // (which is registered after this one) has updated each option.
  onLocaleChange(() => {
    Promise.resolve().then(() => {
      refreshButton();
      if (isOpen) buildMenu();
    });
  });

  refreshButton();

  return {
    root,
    refresh: () => {
      refreshButton();
      if (isOpen) buildMenu();
    },
  };
}
